/* Voice UI-feedback client: capture-side submission (multipart, so it goes
 * around the JSON `api` wrapper the same way product photo uploads do). */

// Mirrored in backend/apps/feedback/models.py (FeedbackReport.REASONS).
export type FeedbackReason = "bug" | "feature" | "improvement" | "question";
export const FEEDBACK_REASONS: FeedbackReason[] = ["bug", "feature", "improvement", "question"];

export type FeedbackSubmission = {
  audio: Blob;
  audioDurationMs: number;
  screenshot: Blob | null;
  pageUrl: string;
  clickX: number;
  clickY: number;
  viewportW: number;
  viewportH: number;
  elementSelector: string;
  elementText: string;
  elementHtml: string;
  reasons: FeedbackReason[];
  language: string;
};

function audioFilename(mime: string): string {
  const base = mime.split(";")[0];
  if (base === "audio/mp4" || base === "video/mp4") return "note.mp4";
  if (base === "audio/ogg") return "note.ogg";
  if (base === "audio/mpeg") return "note.mp3";
  if (base === "audio/wav") return "note.wav";
  return "note.webm";
}

export async function submitFeedbackReport(
  slug: string,
  s: FeedbackSubmission,
): Promise<{ id: number }> {
  const form = new FormData();
  form.append("audio", s.audio, audioFilename(s.audio.type));
  if (s.screenshot) form.append("screenshot", s.screenshot, "screenshot.jpg");
  form.append("page_url", s.pageUrl);
  form.append("click_x", String(Math.round(s.clickX)));
  form.append("click_y", String(Math.round(s.clickY)));
  form.append("viewport_w", String(Math.round(s.viewportW)));
  form.append("viewport_h", String(Math.round(s.viewportH)));
  form.append("element_selector", s.elementSelector);
  form.append("element_text", s.elementText);
  form.append("element_html", s.elementHtml);
  form.append("reasons", JSON.stringify(s.reasons));
  form.append("audio_duration_ms", String(Math.round(s.audioDurationMs)));
  form.append("language", s.language);

  const res = await fetch(`/api/v1/t/${slug}/feedback/reports`, {
    method: "POST",
    body: form,
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error?.message ?? "Feedback upload failed");
  }
  return res.json();
}

/** Readable-but-precise CSS path for the clicked element, e.g.
 *  `div.content > section.card:nth-of-type(2) > button.btn--primary`. */
export function buildSelector(el: Element | null): string {
  const parts: string[] = [];
  let node: Element | null = el;
  while (node && node !== document.body && parts.length < 6) {
    if (node.id) {
      parts.unshift(`#${node.id}`);
      break;
    }
    let part = node.tagName.toLowerCase();
    const classes = Array.from(node.classList)
      .filter((c) => !c.startsWith("svelte-"))
      .slice(0, 2);
    if (classes.length) part += `.${classes.join(".")}`;
    const parent = node.parentElement;
    if (parent) {
      const sameTag = Array.from(parent.children).filter((c) => c.tagName === node!.tagName);
      if (sameTag.length > 1) part += `:nth-of-type(${sameTag.indexOf(node) + 1})`;
    }
    parts.unshift(part);
    node = node.parentElement;
  }
  return parts.join(" > ").slice(0, 500);
}
